import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps a copy of an in-progress form in the browser, so closing the tab,
 * a refresh, or a failed save does not throw the work away.
 *
 * Written after an admin spent two hours building a registration form and hit a
 * validation error on save with no way back to it.
 *
 * The draft is local to the browser and never sent anywhere. It is cleared once
 * the form saves for real.
 */

const PREFIX = 'up:eventDraft:';
const SAVE_DELAY_MS = 800;

export interface DraftState<T> {
  /** A saved draft exists and differs from what is currently on screen. */
  hasDraft: boolean;
  /** When the draft was written. */
  draftSavedAt: Date | null;
  /** When this session last wrote a draft, for the "saved" indicator. */
  lastSavedAt: Date | null;
  restoreDraft: () => void;
  discardDraft: () => void;
  /** Call after a successful save so the draft stops being offered. */
  clearDraft: () => void;
  draft: T | null;
}

export function useFormDraft<T extends Record<string, any>>(
  key: string,
  formData: T,
  applyDraft: (draft: T) => void,
  options: { enabled?: boolean } = {}
): DraftState<T> {
  const { enabled = true } = options;
  const storageKey = `${PREFIX}${key}`;

  const [draft, setDraft] = useState<T | null>(null);
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [dismissed, setDismissed] = useState(false);

  // Read once on mount, before autosave starts overwriting it.
  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.data) {
        setDraft(parsed.data as T);
        setDraftSavedAt(parsed.savedAt ? new Date(parsed.savedAt) : null);
      }
    } catch {
      // Corrupt or unreadable draft is not worth surfacing.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, enabled]);

  // Skip the first render: mounting a form should not immediately write a draft
  // of its untouched initial values.
  const isFirstRun = useRef(true);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      try {
        // File objects cannot be serialised and are not worth keeping anyway.
        const serialisable = { ...formData } as Record<string, any>;
        for (const [k, v] of Object.entries(serialisable)) {
          if (v instanceof File) delete serialisable[k];
        }
        localStorage.setItem(
          storageKey,
          JSON.stringify({ savedAt: new Date().toISOString(), data: serialisable })
        );
        setLastSavedAt(new Date());
      } catch {
        // Storage full or disabled. The form still works.
      }
    }, SAVE_DELAY_MS);

    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [formData, storageKey, enabled]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Nothing useful to do.
    }
    setDraft(null);
    setDraftSavedAt(null);
  }, [storageKey]);

  const restoreDraft = useCallback(() => {
    if (draft) applyDraft(draft);
    setDismissed(true);
  }, [draft, applyDraft]);

  const discardDraft = useCallback(() => {
    clearDraft();
    setDismissed(true);
  }, [clearDraft]);

  return {
    hasDraft: !!draft && !dismissed,
    draftSavedAt,
    lastSavedAt,
    restoreDraft,
    discardDraft,
    clearDraft,
    draft,
  };
}

/** "just now", "4 minutes ago", "yesterday" */
export function describeWhen(when: Date | null): string {
  if (!when) return '';
  const seconds = Math.floor((Date.now() - when.getTime()) / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}
